import { useState, useRef, useCallback, useEffect } from 'react';
import { OnboardingCarouselProps } from './onboarding.types';
import { CommitSlide } from './slides/CommitSlide';
import { TrackSlide } from './slides/TrackSlide';
import { WinSlide } from './slides/WinSlide';
import { analytics } from '@/services/analytics';

const TOTAL_SLIDES = 3;
const SLIDE_NAMES = ['Commit', 'Track', 'Win'];
const SWIPE_THRESHOLD = 50; // Minimum px to trigger swipe

/**
 * Onboarding Carousel
 *
 * Main orchestrator component for the onboarding splash screens.
 * Uses a horizontal slide strip with translateX for smooth GPU-accelerated transitions.
 * All slides are rendered simultaneously to prevent flash during transitions.
 */
export function OnboardingCarousel({
  onLogin,
  onSkip,
  isLoading,
}: OnboardingCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Track onboarding started on mount
  useEffect(() => {
    analytics.track.onboardingStarted();
  }, []);

  // Track step completion when index changes
  useEffect(() => {
    if (currentIndex > 0) {
      analytics.track.onboardingStepCompleted({
        step: currentIndex,
        stepName: SLIDE_NAMES[currentIndex - 1],
      });
    }
  }, [currentIndex]);

  // Touch gesture tracking
  const touchStartX = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const goToNext = useCallback(() => {
    if (currentIndex < TOTAL_SLIDES - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX.current;

    // Add resistance at edges
    let offset = diff;
    if ((currentIndex === 0 && diff > 0) || (currentIndex === TOTAL_SLIDES - 1 && diff < 0)) {
      offset = diff * 0.3; // Rubber band effect
    }

    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset < 0) {
        // Swiped left - go to next
        goToNext();
      } else {
        // Swiped right - go to previous
        goToPrev();
      }
    }

    setDragOffset(0);
    touchStartX.current = 0;
  };

  // Calculate the transform value
  // Note: translateX % is relative to the element's own width (300% of viewport)
  // So we divide by TOTAL_SLIDES to get the correct viewport-relative offset
  const getTransform = () => {
    const slideWidth = 100 / TOTAL_SLIDES; // 33.33% per slide
    const baseOffset = -currentIndex * slideWidth;
    const dragPercent = containerRef.current
      ? (dragOffset / containerRef.current.offsetWidth) * 100
      : 0;
    return `translateX(${baseOffset + dragPercent}%)`;
  };

  // Common props for all slides
  const commonProps = {
    onSkip: handleSkip,
    isActive: true,
    direction: null as 'left' | 'right' | null,
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 h-dvh w-full overflow-hidden bg-[#102210] touch-none"
      style={{
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides Container - Horizontal Strip */}
      <div
        className="flex h-full"
        style={{
          width: `${TOTAL_SLIDES * 100}%`,
          transform: getTransform(),
          transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
        }}
      >
        {/* Slide 1 - Commit */}
        <div className="h-full w-full flex-shrink-0" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
          <CommitSlide {...commonProps} onNext={goToNext} />
        </div>

        {/* Slide 2 - Track */}
        <div className="h-full w-full flex-shrink-0" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
          <TrackSlide {...commonProps} onNext={goToNext} />
        </div>

        {/* Slide 3 - Win */}
        <div className="h-full w-full flex-shrink-0" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
          <WinSlide
            {...commonProps}
            onNext={() => {}}
            onLogin={onLogin}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

// Export all components for direct access if needed
export { CommitSlide } from './slides/CommitSlide';
export { TrackSlide } from './slides/TrackSlide';
export { WinSlide } from './slides/WinSlide';
export { PaginationDots } from './PaginationDots';
